/**
 * Error Boundary Component
 *
 * @package Enterprise\BookingSystem\Components
 */

/**
 * External dependencies
 */
import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';

type Props = {
	readonly children: ReactNode;
	readonly fallback?: (error: Error, reset: () => void) => ReactNode;
	readonly onError?: (error: Error, errorInfo: ErrorInfo) => void;
};

type State = {
	readonly error: Error | null;
	readonly hasError: boolean;
};

// eslint-disable-next-line react/require-optimization
export class ErrorBoundary extends Component<Props, State> {
	static defaultProps = {
		fallback: undefined,
		onError: undefined,
	};

	// eslint-disable-next-line react/state-in-constructor
	state: State = { error: null, hasError: false };

	// eslint-disable-next-line react/sort-comp -- perfectionist/sort-classes requires static properties before static methods
	static getDerivedStateFromError(error: Error): State {
		return { error, hasError: true };
	}

	componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
		const { onError } = this.props;
		console.error('[ErrorBoundary] Caught error:', error, errorInfo);

		onError?.(error, errorInfo);

		if (globalThis.ebsData?.sentryDsn) {
			// TODO: Send to Sentry/Rollbar/etc
		}
	}

	handleReset = () => {
		// eslint-disable-next-line react/no-set-state
		this.setState({ error: null, hasError: false });
	};

	render() {
		const { children, fallback } = this.props;
		const { error, hasError } = this.state;

		if (hasError && error) {
			if (fallback) {
				return fallback(error, this.handleReset);
			}

			return (
				<div className="ebs-error-boundary">
					<div className="ebs-error-boundary__content">
						<h3 className="ebs-error-boundary__title">Something went wrong</h3>
						<p className="ebs-error-boundary__message">{error.message}</p>
						<button
							type="button"
							className="ebs-error-boundary__button"
							onClick={this.handleReset}
						>
							Try again
						</button>
					</div>
				</div>
			);
		}

		// eslint-disable-next-line react/jsx-no-useless-fragment -- needed to return ReactNode (not JSX.Element) and satisfy Component.render() return type
		return <>{children}</>;
	}
}

export const CartErrorFallback = ({
	error,
	reset,
}: {
	readonly error: Error;
	readonly reset: () => void;
}) => (
	<div className="ebs-cart-error">
		<h4>Unable to load cart</h4>
		<p>{error.message}</p>
		<button type="button" onClick={reset}>
			Retry
		</button>
	</div>
);

export const AddToCartErrorFallback = ({
	error,
	reset,
}: {
	readonly error: Error;
	readonly reset: () => void;
}) => (
	<div className="ebs-add-to-cart-error">
		<h4>Unable to add to cart</h4>
		<p>{error.message}</p>
		<button type="button" onClick={reset}>
			Retry
		</button>
	</div>
);
